<template>
  <div class="flex flex-col">
    <div class="max-w-448 w-full mx-auto my-24">
      <Breadcrumb v-if="!isMobile" href="/account/settings" header="Account" :anchorLink="true" class="px-16 sm:px-0" />
      <Card class="flex flex-col items-center mt-24 sm:p-24">
        <h1 class="text-h3 sm:text-h1 text-center">We're sorry to see you go</h1>
        <HiddenProfileIllustration class="mt-32" />
        <div class="w-full text-left">
          <h2 class="text-strong-body sm:text-h4 mt-24">Deactivate your account?</h2>
          <p class="text-small mt-8">
            A deactivated account will not be visible or able to receive messages. All jobs will no longer appear in search.
          </p>
          <p v-if="hasActiveIapSubscription" class="text-small mt-24">
            <span class="font-medium"
              >You purchased your Premium Membership via our iOS app and you will need to cancel the subscription through Apple.
            </span>
            <span>Manage your <a href="https://apps.apple.com/account/subscriptions" class="underline">subscriptions</a>.</span>
          </p>
          <p v-if="hasPremiumWebSubscription" class="text-small mt-24">
            Deactivating your account will immediately end your Premium membership access. The automatic renewal of your
            subscription will be turned off.
          </p>
          <Button
            :value="isLoadingCtlData ? 'Loading...' : 'Deactivate Account'"
            variant="primary"
            size="medium"
            class="w-full mt-24"
            :href="isLoadingCtlData ? '#' : deactivateBtnProps.href"
            :disabled="isLoadingCtlData"
          />
          <div v-if="hasActiveIapSubscription || hasPremiumWebSubscription">
            <h2 class="text-strong-body sm:text-h4 mt-48">Cancel subscription?</h2>
            <p class="text-small mt-8">
              To stop automatic payments and still be able to browse caregivers, cancel your subscription.
            </p>
          </div>
          <Button
            v-if="!isMobile || (isMobile && !isBasic)"
            :value="cancelBtnProps.value"
            variant="outlined"
            size="medium"
            class="w-full mt-24"
            :href="cancelBtnProps.href"
          />
        </div>
      </Card>
      <div v-if="hasActiveIapSubscription || hasPremiumWebSubscription" class="mt-24 px-16 sm:px-0">
        <p class="text-strong-body">Have questions about your subscription?</p>
        <p class="text-small">Visit the <a class="underline" href="https://support.sittercity.com/hc/en-us">help center</a></p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import Card from '@/components/card/Card.vue';
import HiddenProfileIllustration from '@/graphics/illustrations/HiddenProfileIllustration.vue';
import Button from '@/components/button/Button.vue';

export default {
  name: 'SorryToSeeYouGo',
  components: { Breadcrumb, Card, HiddenProfileIllustration, Button },
  props: {
    ctlData: {
      type: Object,
      default: null,
    },
    isMobile: {
      default: false,
      type: Boolean,
    },
    isLoadingCtlData: {
      default: true,
      type: Boolean,
    },
  },
  computed: {
    ...mapGetters('account', ['getAccount']),
    account() {
      return this.getAccount;
    },
    hasActiveIapSubscription(): boolean {
      return this.ctlData?.hasActiveIapSubscription;
    },
    hasPremiumWebSubscription(): boolean {
      return this.account?.isPremium() && !this.hasActiveIapSubscription && !this.ctlData?.hasCanceledSubscription;
    },
    isBasic(): boolean {
      return !(this.hasActiveIapSubscription || this.hasPremiumWebSubscription);
    },
    cancelBtnProps(): object {
      if (this.hasActiveIapSubscription) {
        return { value: 'Manage Apple Subscription', href: 'https://apps.apple.com/account/subscriptions' };
      }
      if (this.hasPremiumWebSubscription) {
        return { value: 'Cancel subscription', href: '/account/subscription_settings' };
      }
      return { value: 'Cancel', href: '/account/settings' };
    },
    deactivateBtnProps(): object {
      if (this.ctlData?.isWinBackEligible) {
        return { href: '/account/discount-offer?entry=deactivate' };
      }
      return { href: '/account/deactivation/questionnaire' };
    },
    accountType(): string {
      if (this.hasActiveIapSubscription) {
        return 'premium_iap';
      }
      if (this.hasPremiumWebSubscription) {
        return 'premium_web';
      }
      return '';
    },
  },
  async mounted() {
    if (this.accountType) sessionStorage.setItem('account_type', this.accountType);
  },
};
</script>
